import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Spinner, Modal, Alert } from 'react-bootstrap';
import { getTranslate } from 'react-localize-redux';

import { order_address_setter, order_city_setter, order_shipping_setter } from '../../actions/cartActions';
import { conf } from '../../confg';
import { errorAction } from '../../actions/request_error';
import AddressForm from '../profileInfo/Forms/address/list/AddressForm';
import AddressList from './AddressList';
import { Truck } from 'lucide-react';

const AddressSection = ({
  onAddressSelect,
  onCityChange,
  onInPersonPickup,
  showError = false, // پراپ جدید برای نمایش ارور
}) => {
  // Redux state
  const dispatch = useDispatch();
  const translate = useSelector((state) => getTranslate(state.localize));
  const orderAddress = useSelector((state) => state.cart.order_address);
  const orderCity = useSelector((state) => state.cart.order_city);
  const user = useSelector((state) => state.user);

  // Local state
  const [addresses, setAddresses] = useState([]);
  const [newAddressId, setNewAddressId] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [showAddressList, setShowAddressList] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addressId, setAddressId] = useState(orderAddress);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [addressDetails, setAddressDetails] = useState({});
  const addressFooterRef = useRef(null);

  // Refs to prevent unnecessary re-renders
  const onCityChangeRef = useRef(onCityChange);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onInPersonPickupRef = useRef(onInPersonPickup);

  // Update refs
  useEffect(() => {
    onCityChangeRef.current = onCityChange;
    onAddressSelectRef.current = onAddressSelect;
    onInPersonPickupRef.current = onInPersonPickup;
  }, [onCityChange, onAddressSelect, onInPersonPickup]);

  // Load addresses from API - بدون dependency های مشکل ساز
  const loadAddresses = useCallback(async () => {
    setLoadingAddress(true);

    try {
      const response = await axios({
        url: conf.server_url + 'addresses',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.length > 0) {
        setAddresses(response.data);

        // Set default address if none selected - فقط در بار اول
        if (!isInitialized && !orderAddress && response.data.length > 0) {
          const defaultAddress = response.data[0];
          dispatch(order_address_setter(defaultAddress.address_book_id));
          dispatch(order_city_setter(defaultAddress.entry_city));

          // Auto-select post shipping for non-Tehran addresses
          if (defaultAddress.entry_city !== 'تهران') {
            dispatch(order_shipping_setter('2'));
          }

          // Notify parent component about city change
          if (onCityChangeRef.current) {
            onCityChangeRef.current(defaultAddress.entry_city);
          }
        }

        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      dispatch(errorAction('خطا در بارگذاری آدرس‌ها'));
    } finally {
      setLoadingAddress(false);
    }
  }, [dispatch, orderAddress, isInitialized]); // فقط dependency های ضروری

  // Handle address selection
  const handleAddressSelect = useCallback(
    (selectedAddressId) => {
      const selectedAddress = addresses.find((addr) => addr.address_book_id === selectedAddressId);

      if (selectedAddress) {
        dispatch(order_address_setter(selectedAddressId));
        dispatch(order_city_setter(selectedAddress.entry_city));
        setAddressId(selectedAddressId);

        // Notify parent component
        if (onAddressSelectRef.current) {
          onAddressSelectRef.current(selectedAddress);
        }
      }
    },
    [addresses, dispatch],
  );

  // Get selected address details
  const getSelectedAddress = useCallback(() => {
    return addresses.find((addr) => addr.address_book_id === addressId) || addresses[0];
  }, [addresses, addressId]);

  // Format address for display
  const formatSelectedAddress = useCallback(() => {
    const selectedAddress = getSelectedAddress();
    if (!selectedAddress) return null;

    const { entry_firstname, entry_lastname, entry_state, entry_city, entry_street_address, pelak, vahed, entry_postcode } = selectedAddress;

    const addressParts = [];
    if (entry_state) addressParts.push(`استان ${entry_state}`);
    if (entry_city) addressParts.push(`شهرستان ${entry_city}`);
    if (entry_street_address) addressParts.push(entry_street_address);
    if (pelak && pelak !== 0) addressParts.push(`پلاک ${pelak}`);
    if (vahed && vahed !== 0) addressParts.push(`واحد ${vahed}`);

    return {
      receiverName: `${entry_firstname} ${entry_lastname}`,
      fullAddress: addressParts.join('، '),
      postalCode: entry_postcode && entry_postcode !== 'undefined' ? entry_postcode : null,
    };
  }, [getSelectedAddress]);

  // Handle modal close
  const handleAddModalClose = useCallback(() => {
    setShowAddModal(false);
  }, []);

  // Handle successful address add
  const handleAddressAdded = useCallback(
    (newAddressId) => {
      loadAddresses();
      if (newAddressId) {
        setNewAddressId(newAddressId);
      }
      setShowAddModal(false);
    },
    [loadAddresses],
  );

  useEffect(() => {
    if (newAddressId) {
      handleAddressSelect(newAddressId);
    }
  }, [newAddressId, handleAddressSelect, addresses]);
  // Handle show add new address
  const handleShowAddAddress = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleInPersonPickup = useCallback(() => {
    dispatch(order_address_setter(1));
    dispatch(order_city_setter('تهران'));
    dispatch(order_shipping_setter('3'));
    if (onInPersonPickupRef.current) {
      onInPersonPickupRef.current();
    }
  }, [dispatch]);

  // Effects
  // فقط یکبار در شروع load کن
  useEffect(() => {
    if (addresses.length === 0 && !loadingAddress) {
      loadAddresses();
    }
  }, []); // خالی - فقط یکبار اجرا میشه

  // Update local addressId when orderAddress changes
  useEffect(() => {
    setAddressId(orderAddress);
  }, [orderAddress]);

  // Render loading state
  if (loadingAddress && addresses.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '10px' }}>
        <Spinner animation='border' className='mx-2' />
      </div>
    );
  }

  const selectedAddressInfo = formatSelectedAddress();

  return (
    <div className={`address-section ${showError ? 'has-error' : ''}`}>
      {/* Address Card */}
      <div className='modern-address-card'>
        {selectedAddressInfo ? (
          <div className='address-content-wrapper'>
            <div className='address-main-content'>
              <div className='address-details'>
                <div className='address-label'>ارسال به آدرس انتخاب شده *</div>
                <div className='receiver-info'>{selectedAddressInfo.receiverName}</div>
                <div className='address-text'>
                  {selectedAddressInfo.fullAddress}
                  {selectedAddressInfo.postalCode && <span className='postal-code'> کد پستی: {selectedAddressInfo.postalCode}</span>}
                </div>
              </div>
              <div className='address-icon-section'>
                <div className='address-icon-2'>
                  <Truck />
                </div>
              </div>
            </div>

            {addresses.length > 0 && (
              <div className='change-address-section'>
                <button className='change-address-btn' onClick={() => setShowAddressList(true)}>
                  تغییر آدرس
                  <i className='fas fa-angle-left'></i>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className='no-address-content'>
            <div className='no-address-icon'>
              <i className='fas fa-map-marker-alt'></i>
            </div>
            <div className='no-address-text'>
              <div className='no-address-title'>آدرسی یافت نشد</div>
              <div className='no-address-subtitle'>برای ادامه خرید آدرس خود را اضافه کنید</div>
            </div>
            <button className='add-address-btn' onClick={handleShowAddAddress}>
              <i className='fas fa-plus'></i>
              اضافه کردن آدرس جدید
            </button>
            <button className='in-person-pickup-btn' onClick={handleInPersonPickup}>
              حضوری میخوام تحویل بگیرم
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        .address-section {
          margin-bottom: 0rem;
        }

        /* استایل کانتینر در حالت ارور */
        .address-section.has-error .modern-address-card {
          border-color: #dc3545;
        }

        /* پیام ارور */
        .address-error-alert {
          margin-bottom: 15px;
          border: none;
          border-radius: 8px;
          background-color: #f8d7da;
          border-left: 4px solid #dc3545;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .error-content {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #721c24;
          font-weight: 500;
        }

        .error-content i {
          color: #dc3545;
          font-size: 14px;
        }

        .modern-address-card {
          background: white;
          border: 2px solid #01494b;
          margin-bottom: 15px;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 12px rgba(0, 123, 255, 0.08);
          transition: all 0.3s ease;
        }

        .modern-address-card:hover {
          border-color: #01494b;
          box-shadow: 0 4px 20px rgba(0, 123, 255, 0.15);
        }

        .address-content-wrapper {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .address-main-content {
          display: flex;
          text-align: right;
          gap: 1rem;
          flex: 1;
          flex-direction: row-reverse !important;
        }

        .address-icon-section {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 50px;
        }

        .address-icon-2 {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #01494b;
          font-size: 20px;
        }

        .address-details {
          flex: 1;
        }

        .address-label {
          font-size: 0.85rem;
          color: #01494b;
          font-weight: 600;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .receiver-info {
          font-size: 1.1rem;
          font-weight: 600;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .address-text {
          font-size: 0.95rem;
          color: #666;
          line-height: 1.5;
        }

        .postal-code {
          color: #888;
          font-size: 0.9rem;
        }

        .change-address-section {
          display: flex;
          align-items: center;
        }

        .change-address-btn {
          background: none;
          border: 2px solid #01494b;
          color: #01494b;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .change-address-btn:hover {
          background: #01494b;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
        }

        .no-address-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2rem 1rem;
        }

        .no-address-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #f8f9fa, #e9ecef);
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          font-size: 24px;
          margin-bottom: 1rem;
        }

        .no-address-text {
          margin-bottom: 1.5rem;
        }

        .no-address-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .no-address-subtitle {
          font-size: 0.9rem;
          color: #666;
        }

        .add-address-btn {
          background: #01494b;
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
        }

        .add-address-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
        }

        .in-person-pickup-btn {
          background: white;
          color: #01494b;
          border: 1px solid #01494b;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.95rem;
          margin-top: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .in-person-pickup-btn:hover {
          background: #01494b;
          color: white;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .address-content-wrapper {
            flex-direction: column;
            gap: 1rem;
          }

          .address-main-content {
            gap: 0.75rem;
          }

          .address-label {
            font-size: 0.8rem;
            margin-bottom: 0.4rem;
          }

          .receiver-info {
            font-size: 0.8rem;
            margin-bottom: 0.4rem;
          }

          .address-text {
            font-size: 0.9rem;
            line-height: 1.4;
          }

          .postal-code {
            font-size: 0.85rem;
          }

          .change-address-section {
            width: 100%;
          }

          .change-address-btn {
            width: 100%;
            justify-content: space-between;
            padding: 1rem 1.25rem;
            font-size: 0.7rem;
          }

          .modern-address-card {
            padding: 1rem;
            border-radius: 12px;
            margin: 0px;
            margin-bottom: 10px;
          }

          .no-address-content {
            padding: 1.5rem 1rem;
          }

          .no-address-icon {
            width: 50px;
            height: 50px;
            font-size: 20px;
            border-radius: 12px;
          }

          .no-address-title {
            font-size: 1rem;
          }

          .no-address-subtitle {
            font-size: 0.85rem;
          }

          .add-address-btn {
            padding: 0.875rem 1.75rem;
            font-size: 0.95rem;
            border-radius: 10px;
          }

          .in-person-pickup-btn {
            width: 100%;
            padding: 0.8rem 1rem;
            font-size: 0.9rem;
          }

          .error-content {
            font-size: 12px;
          }
        }

        @media (max-width: 480px) {
          .modern-address-card {
            margin: 0px;
            margin-bottom: 10px !important;
            border-radius: 10px;
          }

          .address-main-content {
            align-items: flex-start;
          }

          .change-address-btn {
            padding: 0.875rem 1rem;
            border-radius: 10px;
          }
        }
      `}</style>

      {/* Address List Component */}
      <AddressList
        addresses={addresses}
        selectedAddressId={addressId}
        onAddressSelect={handleAddressSelect}
        show={showAddressList}
        onHide={() => setShowAddressList(false)}
        onAddNewAddress={handleShowAddAddress}
        isEditModalOpen={isEditModalOpen}
        setIsEditModalOpen={setIsEditModalOpen}
        setAddressDetails={setAddressDetails}
      />

      {/* Modal for adding new address */}
      <Modal dialogClassName='modal-address-dialog' show={showAddModal} onHide={handleAddModalClose} style={{ zIndex: '10000000000' }}>
        <Modal.Header closeButton>
          <Modal.Title style={{ direction: 'rtl' }}>{isEditModalOpen ? translate('add-address') : 'ویرایش آدرس'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0px' }}>
          <AddressForm
            footerContainerRef={addressFooterRef}
            hasButtonNavigation={false}
            hasTitle={false}
            hasMarginButton={false}
            title='اضافه کردن آدرس جدید'
            setIsAddMenuOpen={handleAddModalClose}
            type={isEditModalOpen ? 'edit' : 'add'}
            handleReload={handleAddressAdded}
            {...(isEditModalOpen ? addressDetails : {})}
          />
        </Modal.Body>
        <Modal.Footer className='modal-address-footer' style={{ justifyContent: 'center', borderTop: '1px solid #eee' }}>
          <div ref={addressFooterRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

AddressSection.defaultProps = {
  onAddressSelect: null,
  onCityChange: null,
  onInPersonPickup: null,
  showError: false,
  errorMessage: 'آدرس را انتخاب کنید',
};

export default AddressSection;
